import { useState, useEffect, useCallback } from "react";
import { X, ChevronLeft, ChevronRight, ZoomIn, ZoomOut } from "lucide-react";
import { Button } from "@/components/ui/button";
import { BRAND_COLORS } from "@/lib/menuData";

// Menu images with titles
const MENU_IMAGES = [
  {
    id: "breakfast",
    title: "Breakfast Menu",
    description: "Island Chevron & Grill - Morning Favorites",
    url: "https://customer-assets.emergentagent.com/job_menuhub-manager/artifacts/7iuf442t_Screenshot%202026-02-12%20112409.png",
  },
  {
    id: "lunch",
    title: "Daily Specials",
    description: "Island Chevron & Grill - Burgers, Sandwiches & More",
    url: "https://customer-assets.emergentagent.com/job_menuhub-manager/artifacts/kui15m68_Screenshot%202026-02-12%20112504.png",
  },
];

export default function MenuGallery({ open, onClose }) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isZoomed, setIsZoomed] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [touchStart, setTouchStart] = useState(null);

  // Reset state when opening
  useEffect(() => {
    if (open) {
      setCurrentIndex(0);
      setIsZoomed(false);
      setImageLoaded(false);
    }
  }, [open]);

  // Handle keyboard navigation
  useEffect(() => {
    if (!open) return;

    const handleKeyDown = (e) => {
      switch (e.key) {
        case "Escape":
          onClose();
          break;
        case "ArrowLeft":
          goToPrev();
          break;
        case "ArrowRight":
          goToNext();
          break;
        default:
          break;
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [open, currentIndex]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (open) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  const goToNext = useCallback(() => {
    setImageLoaded(false);
    setIsZoomed(false);
    setCurrentIndex((prev) => (prev + 1) % MENU_IMAGES.length);
  }, []);

  const goToPrev = useCallback(() => {
    setImageLoaded(false);
    setIsZoomed(false);
    setCurrentIndex((prev) => (prev - 1 + MENU_IMAGES.length) % MENU_IMAGES.length);
  }, []);

  // Touch handlers for swipe
  const handleTouchStart = (e) => {
    setTouchStart(e.touches[0].clientX);
  };

  const handleTouchEnd = (e) => {
    if (!touchStart) return;
    const touchEnd = e.changedTouches[0].clientX;
    const diff = touchStart - touchEnd;

    if (Math.abs(diff) > 50) {
      if (diff > 0) {
        goToNext();
      } else {
        goToPrev();
      }
    }
    setTouchStart(null);
  };

  if (!open) return null;

  const currentImage = MENU_IMAGES[currentIndex];

  return (
    <div 
      className="fixed inset-0 z-[100] flex flex-col"
      style={{ backgroundColor: "rgba(0, 0, 0, 0.95)" }}
      data-testid="menu-gallery-modal"
    >
      {/* Header */}
      <div className="flex items-center justify-between p-4 bg-black/50 backdrop-blur-sm">
        <div className="flex items-center gap-4">
          <span className="text-white/80 text-sm font-medium">
            {currentIndex + 1} / {MENU_IMAGES.length}
          </span>
          <h2 className="text-white font-bold text-lg hidden sm:block" style={{ fontFamily: "Montserrat" }}>
            {currentImage.title}
          </h2>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="icon"
            className="text-white hover:bg-white/20 rounded-full"
            onClick={() => setIsZoomed(!isZoomed)}
            data-testid="zoom-toggle-btn"
          >
            {isZoomed ? <ZoomOut className="h-5 w-5" /> : <ZoomIn className="h-5 w-5" />}
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="text-white hover:bg-white/20 rounded-full"
            onClick={onClose}
            data-testid="close-gallery-btn"
          >
            <X className="h-6 w-6" />
          </Button>
        </div>
      </div>

      {/* Mobile Title */}
      <div className="sm:hidden text-center py-2 bg-black/30">
        <h2 className="text-white font-bold text-base" style={{ fontFamily: "Montserrat" }}>
          {currentImage.title}
        </h2>
        <p className="text-white/60 text-xs">{currentImage.description}</p>
      </div>

      {/* Image Container */}
      <div 
        className="flex-1 relative flex items-center justify-center overflow-hidden"
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >
        {/* Loading Spinner */}
        {!imageLoaded && (
          <div className="absolute inset-0 flex items-center justify-center">
            <div 
              className="w-10 h-10 border-4 border-t-transparent rounded-full animate-spin"
              style={{ borderColor: `${BRAND_COLORS.tropicalAqua} transparent transparent transparent` }}
            />
          </div>
        )}

        {/* Image */}
        <img
          src={currentImage.url}
          alt={currentImage.title}
          className={`max-h-full transition-all duration-300 ${
            isZoomed ? "max-w-none cursor-zoom-out scale-150" : "max-w-full cursor-zoom-in"
          } ${imageLoaded ? "opacity-100" : "opacity-0"}`}
          style={{ objectFit: "contain" }}
          onLoad={() => setImageLoaded(true)}
          onClick={() => setIsZoomed(!isZoomed)}
          draggable={false}
          loading="lazy"
          data-testid="menu-image"
        />

        {/* Navigation Arrows */}
        {MENU_IMAGES.length > 1 && (
          <>
            <Button
              variant="ghost"
              size="icon"
              className="absolute left-2 sm:left-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-black/50 hover:bg-black/70 text-white"
              onClick={goToPrev}
              data-testid="gallery-prev-btn"
            >
              <ChevronLeft className="h-8 w-8" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="absolute right-2 sm:right-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-black/50 hover:bg-black/70 text-white"
              onClick={goToNext}
              data-testid="gallery-next-btn"
            >
              <ChevronRight className="h-8 w-8" />
            </Button>
          </>
        )}
      </div>

      {/* Footer with Dots */}
      <div className="flex items-center justify-center gap-2 p-4 bg-black/50 backdrop-blur-sm">
        {MENU_IMAGES.map((_, idx) => (
          <button
            key={idx}
            className={`w-2.5 h-2.5 rounded-full transition-all ${
              idx === currentIndex ? "bg-white scale-110" : "bg-white/40 hover:bg-white/60"
            }`}
            onClick={() => {
              setImageLoaded(false);
              setIsZoomed(false);
              setCurrentIndex(idx);
            }}
            data-testid={`gallery-dot-${idx}`}
          />
        ))}
      </div>

      {/* Swipe Hint (Mobile) */}
      <p className="sm:hidden text-center text-white/40 text-xs pb-4">
        Swipe left/right to navigate • Tap image to zoom
      </p>
    </div>
  );
}
